import React from 'react';
import { UserIcon } from 'lucide-react';
import { accessByProfile, orgUsers } from '../../data/offboarding';
import { primaryButton } from '../../utils/buttonStyles';
import { PillMultiSelect } from './PillMultiSelect';
import type { PillOption } from './PillMultiSelect';
import { ToolCardHeader } from './ToolCardHeader';
export type OffboardingScope = 'user' | 'records';
interface OffboardingStartProps {
  scope: OffboardingScope | null;
  onScopeChange: (scope: OffboardingScope) => void;
  userId: string | null;
  onUserChange: (userId: string | null) => void;
  onContinue: () => void;
}
const SCOPE_OPTIONS: {
  value: OffboardingScope;
  title: string;
  description: string;
}[] = [{
  value: 'user',
  title: 'Yes, offboard a specific user',
  description: 'Deactivate their licenses and permission sets, then reassign their Person Accounts and Leads.'
}, {
  value: 'records',
  title: 'No, only reassign records',
  description: 'Go straight to reassignment for all inactive owners or a hand-picked set.'
}];
const userOptions: PillOption[] = orgUsers.filter(u => !u.excludedFromTargets).map(u => ({
  id: u.id,
  primary: u.name,
  secondary: u.email,
  tag: u.active ? 'Active' : 'Inactive'
}));
export function OffboardingStart({
  scope,
  onScopeChange,
  userId,
  onUserChange,
  onContinue
}: OffboardingStartProps) {
  const user = orgUsers.find(u => u.id === userId);
  const access = user ? accessByProfile[user.profileId] : undefined;
  const canContinue = scope === 'records' || scope === 'user' && Boolean(user);
  return <section aria-label="Start offboarding" className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" data-id="element-150" data-mp="1ng5ohaimlonb">
      <ToolCardHeader subtitle="Start by telling us what you're offboarding" data-id="element-151" data-mp="1ih2gpib9h4bn" />

      <div className="space-y-6 px-5 py-6 sm:px-6" data-id="element-152" data-mp="c2l6l4f0buod">
        <fieldset data-id="element-153" data-mp="72f6czuo8zuo">
          <legend className="mb-3 text-[15px] font-semibold text-gray-900" data-id="element-154" data-mp="71xfkiqs3du5">
            Is a specific user being offboarded?
          </legend>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-id="element-155" data-mp="2v8vyjwpngp7o">
            {SCOPE_OPTIONS.map(option => {
            const selected = scope === option.value;
            return <label key={option.value} className={`flex cursor-pointer gap-3 rounded-xl border p-4 transition-colors duration-150 ${selected ? 'border-navy-600 bg-navy-50 ring-1 ring-navy-600/20' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-156" data-mp="2l9oi30nmcegc">
                  <input type="radio" name="offboarding-scope" value={option.value} checked={selected} onChange={() => onScopeChange(option.value)} className="mt-0.5 h-4 w-4 flex-shrink-0 cursor-pointer accent-navy-600" data-id="element-157" data-mp="22kzzilclyl7q" />
                  <span data-id="element-158" data-mp="1dyk5wixpjv1s">
                    <span className="block text-sm font-semibold text-gray-900" data-id="element-159" data-mp="1dzvc0pemlew2">{option.title}</span>
                    <span className="mt-1 block text-[13px] text-gray-600" data-id="element-160" data-mp="19ya29cxe1gq3">{option.description}</span>
                  </span>
                </label>;
          })}
          </div>
        </fieldset>

        {scope === 'user' && <div className="max-w-2xl" data-id="element-161" data-mp="1j09lmbvvkww7">
            <PillMultiSelect id="departing-user" label="Departing user" placeholder="Search users by name or email…" helpText={user && access ? `${access.licenses.length} licenses and ${access.permissionSets.length} permission sets will be reviewed next.` : 'Choose the one user leaving the company.'} options={userOptions} selectedIds={userId ? [userId] : []} onChange={ids => onUserChange(ids[ids.length - 1] ?? null)} pillIcon={UserIcon} required data-id="element-162" data-mp="1wwwikentvn24" />
          </div>}
      </div>

      <footer className="flex flex-col gap-3 border-t border-gray-100 px-5 py-5 sm:px-6 md:flex-row md:justify-end" data-id="element-163" data-mp="dyi1t448x1n4">
        <button type="button" onClick={onContinue} disabled={!canContinue} className={`${primaryButton} md:min-w-[120px]`} data-id="element-164" data-mp="iqrqyhxjdxoc">
          {scope === 'user' ? 'Continue to Access' : 'Continue'}
        </button>
      </footer>
    </section>;
}